import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import { 
  Package, 
  ShoppingCart, 
  AlertTriangle, 
  TrendingUp, 
  DollarSign,
  Plus,
  Search,
  Filter,
  Download,
  BarChart3
} from "lucide-react";
import { useBusiness } from "@/providers/business-provider";
import { Sidebar } from "@/components/ui/sidebar";

interface InventoryDashboardData {
  totalValue: number;
  totalItems: number;
  lowStockItems: number;
  recentMovements: number;
  topValueItems: Array<{
    name: string;
    sku: string;
    value: number;
    quantity: number;
  }>;
  reorderAlerts: Array<{
    id: number;
    name: string;
    sku: string;
    currentQuantity: number;
    reorderPoint: number;
  }>;
}

export default function InventoryDashboard() {
  const { activeBusiness } = useBusiness();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");

  const { data: dashboardData, isLoading } = useQuery<InventoryDashboardData | null>({
    queryKey: ["inventory-dashboard", activeBusiness?.id],
    queryFn: async () => {
      if (!activeBusiness?.id) return null;
      const response = await fetch(`/api/businesses/${activeBusiness.id}/inventory/dashboard`);
      if (!response.ok) throw new Error("Failed to fetch inventory dashboard");
      return response.json();
    },
    enabled: !!activeBusiness?.id
  });

  if (isLoading) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <div className="flex-1 md:ml-64 space-y-6 p-6 overflow-y-auto">
          <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex-1 md:ml-64 space-y-6 p-6 overflow-y-auto">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Inventory Management</h1>
          <p className="text-muted-foreground">
            Manage your inventory with automatic COGS calculations
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild>
            <Link href="/inventory/items/new">
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link href="/inventory/purchase">
              <ShoppingCart className="h-4 w-4 mr-2" />
              Record Purchase
            </Link>
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Inventory Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${dashboardData?.totalValue?.toLocaleString() || "0"}
            </div>
            <p className="text-xs text-muted-foreground">
              Current market value
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Items</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData?.totalItems || 0}</div>
            <p className="text-xs text-muted-foreground">
              Active inventory items
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Low Stock Alerts</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {dashboardData?.lowStockItems || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Items below reorder point
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Recent Movements</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData?.recentMovements || 0}</div>
            <p className="text-xs text-muted-foreground">
              Last 30 days
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Reorder Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Reorder Alerts
            </CardTitle>
            <CardDescription>
              Items that need to be restocked
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboardData?.reorderAlerts?.length ?? 0) > 0 ? (
                dashboardData!.reorderAlerts.slice(0, 5).map((alert) => (
                  <div key={alert.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{alert.name}</p>
                      <p className="text-sm text-muted-foreground">SKU: {alert.sku}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="destructive">
                        {alert.currentQuantity} / {alert.reorderPoint}
                      </Badge>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  No reorder alerts at this time
                </p>
              )}
              {(dashboardData?.reorderAlerts?.length ?? 0) > 5 && (
                <Button variant="outline" className="w-full" asChild>
                  <Link href="/inventory/alerts">View All Alerts</Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Value Items */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              Top Value Items
            </CardTitle>
            <CardDescription>
              Highest inventory value items
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboardData?.topValueItems?.length ?? 0) > 0 ? (
                dashboardData!.topValueItems.slice(0, 5).map((item, index) => (
                  <div key={item.sku} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-muted-foreground">
                        SKU: {item.sku} • Qty: {item.quantity}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">${item.value.toLocaleString()}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  No inventory items yet
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Navigation */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Button variant="outline" className="h-20 flex-col gap-2" asChild>
          <Link href="/inventory/items">
            <Package className="h-6 w-6" />
            View All Items
          </Link>
        </Button>
        
        <Button variant="outline" className="h-20 flex-col gap-2" asChild>
          <Link href="/inventory/movements">
            <TrendingUp className="h-6 w-6" />
            Movement History
          </Link>
        </Button>
        
        <Button variant="outline" className="h-20 flex-col gap-2" asChild>
          <Link href="/inventory/reports">
            <BarChart3 className="h-6 w-6" />
            Reports & Analytics
          </Link>
        </Button>
        
        <Button variant="outline" className="h-20 flex-col gap-2" asChild>
          <Link href="/vendors">
            <Package className="h-6 w-6" />
            Manage Vendors
          </Link>
        </Button>
      </div>
      </div>
    </div>
  );
}
